/**
 * The expectation the run dialog collects, and the one place millimetres
 * become metres.
 *
 * Pavement tolerances are quoted in mm and earthworks in m, so the UI takes
 * mm — but storage, GraphQL and every JSON downstream are SI. Converting in
 * exactly one place is what stops a stray factor of 1000 reaching the worker.
 *
 * Follows the `stationVolumes/dialogModel.ts` precedent: one pure module
 * exporting the same four hooks (zod shape, superRefine body, defaultValues
 * fragment, mutation-payload builder), shared by every dialog that asks it
 * (`RunCompareSurfacesDialog`) so they cannot disagree about
 * what the expectation question means.
 */
import { z } from 'zod';

import { PC_METHODS } from '@/components/SurfacePicker';
import type { SurfaceExpectationInput, SurfaceExpectationMode as GqlSurfaceExpectationMode } from '@/graphql/graphql';

export const EXPECTATION_MODES = ['NONE', 'MATCH', 'CONSTANT', 'SURFACES'] as const;
export type ExpectationMode = (typeof EXPECTATION_MODES)[number];

export const mmToM = (mm: number): number => mm / 1000;
export const mToMm = (m: number): number => m * 1000;

export interface ExpectationForm {
  expectMode: ExpectationMode;
  expectThicknessMm?: number | null;
  // The three fields per side that `SurfacePicker` owns. Its field names are
  // `${prefix}LayerId` / `${prefix}SurfaceIds` / `${prefix}PcMethod`, so these
  // names are not a style choice — they are the contract with that component.
  expectUpperLayerId?: string | null;
  expectUpperSurfaceIds?: string[];
  expectUpperPcMethod?: (typeof PC_METHODS)[number];
  expectLowerLayerId?: string | null;
  expectLowerSurfaceIds?: string[];
  expectLowerPcMethod?: (typeof PC_METHODS)[number];
  // No real value — an anchor path for the two SURFACES-mode issues that are
  // about the PAIR, not about either field individually ('surfaceRequired',
  // 'sameSurface'). Anchoring a pair error to one of the two `*LayerId`
  // fields made `SurfacePicker`'s own unconditional `<FormMessage/>` for
  // that field render the raw untranslated tag next to `ExpectationFields`'
  // own correctly-translated sentence (fix round 1). Do not repurpose this
  // for anything with an actual value.
  expectSurfaces?: undefined;
  underTolMm?: number | null;
  overTolMm?: number | null;
  failAreaPct?: number | null;
}

/**
 * `null` for NONE, so the mutation payload is byte-identical to today's for
 * every existing workflow.
 *
 * A tolerance left blank stays `null`, never 0: `null` means "measure this
 * side, never fail on it" and `0` means "any excess at all is a defect".
 * Confusing them changes every verdict the tool produces.
 */
export function expectationPayload(f: ExpectationForm): SurfaceExpectationInput | null {
  if (f.expectMode === 'NONE') return null;
  const mm = (v: number | null | undefined) => (v == null ? null : mmToM(v));
  return {
    // `SurfaceExpectationMode` is a real TS enum in the generated client, so a
    // plain string literal needs the same cast the rest of the client uses
    // for GraphQL enums (e.g. `'VIEWER' as ProductLine` in subscription.atom.ts).
    mode: f.expectMode as GqlSurfaceExpectationMode,
    // MATCH is CONSTANT with t = 0, so it sends no thickness at all.
    ...(f.expectMode === 'CONSTANT' && f.expectThicknessMm != null
      ? { thicknessM: mmToM(f.expectThicknessMm) }
      : {}),
    // A full `SurfaceInput` per side, the same shape the subject pair sends:
    // `pcMethod` decides how a point-cloud expectation surface is reduced
    // (`min` for a top-of-layer capture is a DIFFERENT surface from `mean`),
    // and `surfaceIds` narrows a multi-TIN LandXML to the one design layer.
    // Both are already supported end to end; dropping them here would silently
    // fall back to the whole file at `min`.
    ...(f.expectMode === 'SURFACES' && f.expectUpperLayerId && f.expectLowerLayerId
      ? {
          upper: {
            layerId: f.expectUpperLayerId,
            surfaceIds: f.expectUpperSurfaceIds?.length ? f.expectUpperSurfaceIds : undefined,
            pcMethod: f.expectUpperPcMethod ?? 'min',
          },
          lower: {
            layerId: f.expectLowerLayerId,
            surfaceIds: f.expectLowerSurfaceIds?.length ? f.expectLowerSurfaceIds : undefined,
            pcMethod: f.expectLowerPcMethod ?? 'min',
          },
        }
      : {}),
    underToleranceM: mm(f.underTolMm),
    overToleranceM: mm(f.overTolMm),
    failAreaPct: f.failAreaPct ?? 5,
  };
}

/** Spread into both dialogs' `z.object({...})`. Every field optional and
 *  nullable, so a dialog that never touches the question still validates. */
export const expectationZodShape = {
  expectMode: z.enum(EXPECTATION_MODES).default('NONE'),
  expectThicknessMm: z.number().nullable().optional(),
  expectUpperLayerId: z.string().nullable().optional(),
  expectUpperSurfaceIds: z.array(z.string()).default([]),
  expectUpperPcMethod: z.enum(PC_METHODS).default('min'),
  expectLowerLayerId: z.string().nullable().optional(),
  expectLowerSurfaceIds: z.array(z.string()).default([]),
  expectLowerPcMethod: z.enum(PC_METHODS).default('min'),
  // Carries no value — exists only so `refineExpectation` has a schema path
  // to anchor the pair-level SURFACES issues to (fix round 1). Do not
  // "clean up" this field; removing it makes `ctx.addIssue({path:
  // ['expectSurfaces']})` target a path the schema doesn't know about.
  expectSurfaces: z.undefined().optional(),
  underTolMm: z.number().nullable().optional(),
  overTolMm: z.number().nullable().optional(),
  failAreaPct: z.number().nullable().optional(),
};

/** Spread into both dialogs' `defaultValues`. NONE, so both dialogs open
 *  exactly as they do today. */
export const expectationDefaults = (): ExpectationForm => ({
  expectMode: 'NONE',
  expectThicknessMm: null,
  expectUpperLayerId: null,
  expectUpperSurfaceIds: [],
  expectUpperPcMethod: 'min',
  expectLowerLayerId: null,
  expectLowerSurfaceIds: [],
  expectLowerPcMethod: 'min',
  expectSurfaces: undefined,
  underTolMm: null,
  overTolMm: null,
  failAreaPct: 5,
});

/**
 * Cross-field validation, called from each dialog's existing `.superRefine`.
 *
 * This mirrors `assertExpectation` on the server (Task 9) on purpose: the
 * server is the authority and must keep its checks, but a user who mistypes a
 * tolerance deserves an inline message on the field, not a job that starts,
 * costs money, and fails minutes later.
 *
 * Each `message` is an i18n key looked up by the dialog's `onInvalid`
 * mapping, the same way the existing `sameLayer` tag already is — never a
 * literal English sentence.
 */
export function refineExpectation(d: ExpectationForm, ctx: z.RefinementCtx): void {
  if (d.expectMode === 'NONE') return;
  const issue = (path: keyof ExpectationForm, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

  if (d.expectMode === 'CONSTANT' && !(typeof d.expectThicknessMm === 'number' && d.expectThicknessMm > 0))
    issue('expectThicknessMm', 'thicknessRequired');

  if (d.expectMode === 'SURFACES') {
    // These two are about the PAIR, not about either field individually —
    // anchored to the synthetic `expectSurfaces` path (not `*LayerId`) so
    // `SurfacePicker`'s own unconditional `<FormMessage/>` for `${prefix}LayerId`
    // never picks up the raw tag (fix round 1).
    if (!d.expectUpperLayerId) issue('expectSurfaces', 'surfaceRequired');
    if (!d.expectLowerLayerId) issue('expectSurfaces', 'surfaceRequired');
    if (d.expectUpperLayerId && d.expectUpperLayerId === d.expectLowerLayerId)
      issue('expectSurfaces', 'sameSurface');
  }

  for (const k of ['underTolMm', 'overTolMm'] as const)
    if (typeof d[k] === 'number' && (d[k] as number) < 0) issue(k, 'toleranceNegative');

  // Both blank means nothing can ever fail, so the verdict would be a
  // foregone PASS. Better to say so than to return a meaningless green chip.
  if (d.underTolMm == null && d.overTolMm == null) issue('underTolMm', 'toleranceRequired');

  // Matches the server's bound EXACTLY (`expectationValidation.ts`:
  // `v < 0 || v > 100`), so a value the client accepts can never be the one
  // that starts a job the server then throws on — that would burn a batch
  // row and real compute for nothing.
  if (typeof d.failAreaPct === 'number' && (d.failAreaPct < 0 || d.failAreaPct > 100))
    issue('failAreaPct', 'failAreaPctRange');
}

/** Minimal input `preflight` needs — deliberately NOT `ExpectationForm`. The
 *  RHF-prefixed field names (`expectMode`, `expectThicknessMm`, …) are the
 *  contract with `SurfacePicker`/the form, not something a pure advice
 *  function should have to know about; the dialog picks the two or three
 *  fields that matter and the one extra piece `refineExpectation` never
 *  sees — the run's effective grid resolution. */
export interface ExpectationPreflightInput {
  mode: ExpectationMode;
  thicknessMm?: number | null;
  underTolMm?: number | null;
  overTolMm?: number | null;
  /** The run's actual (possibly coarsened) grid resolution, in metres —
   *  `resolution` for a surface difference, `stationGridResolution(volumeResolution)`
   *  for a deviation run (spec §10.2 step 2's floor). `undefined`/`null`/`<=0`
   *  means "not known yet" and skips the resolution-dependent check. */
  effectiveResolutionM?: number | null;
}

/**
 * `level` is the literal `'warn'` — deliberately, not `string`. There is no
 * `block` level in this union at all (see `preflight`'s doc comment below),
 * so "this never blocks" is a compile-time guarantee at every call site
 * rather than something a test has to assert on one array. (Fix round 1: an
 * earlier version widened this to `string` to keep a leftover
 * `c.level === 'block'` test — from a draft where a `block` level existed —
 * type-checking; the fix was to delete that assertion, not the type.)
 */
export interface ExpectationWarning {
  level: 'warn';
  key: string;
  params?: Record<string, unknown>;
}

const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

/**
 * Advisory-only pre-flight checks for the expectation question — never a
 * blocker. `refineExpectation` (zod) already owns everything hard-invalid
 * about a SINGLE field; `preflight` only speaks up about advice that needs
 * ANOTHER form field the zod schema does not have, namely the run's
 * effective resolution. There is deliberately no `block` level in
 * `ExpectationWarning` — see the design doc §13.1 and the task-12 brief:
 * two mechanisms rejecting one submit for one mistake shows the user two
 * messages for one problem.
 */
export function preflight(input: ExpectationPreflightInput): ExpectationWarning[] {
  if (input.mode === 'NONE') return [];
  const warnings: ExpectationWarning[] = [];

  const res = input.effectiveResolutionM;
  if (typeof res === 'number' && res > 0) {
    // A tenth of a cell is a FLOOR, not the truth — the real bound is the
    // survey's own uncertainty, which the tool does not yet carry (design
    // doc §13.1). Below it the result is measuring grid noise, not the
    // actual thickness, but the user may still legitimately want the map
    // at this tolerance, so this only ever warns.
    const floorMm = (res * 1000) / 10;
    // `t >= 0`, not `t > 0` (fix round 2, item 2): a ZERO tolerance is a
    // real, deliberate value in this codebase's semantics — it means "any
    // excess at all is a defect" (see `expectationPayload`'s doc comment on
    // null-vs-0). On a coarse grid that is the STRONGEST case for this
    // warning, not an exemption from it: zero margin is guaranteed to
    // report grid noise as deviation, more so than the 5 mm case this
    // already warns for. A negative tolerance can still slip through here
    // mid-edit (before `refineExpectation`'s own `toleranceNegative` rule
    // has run) — `t >= 0` excludes it, same as before.
    const breaching = [input.underTolMm, input.overTolMm].filter(
      (t): t is number => typeof t === 'number' && t >= 0 && t < floorMm,
    );
    if (breaching.length > 0) {
      // Deliberate (fix round 1, item 3): ONE consolidated warning citing
      // the finer of the two breaching tolerances, not one per field — the
      // "±{{mm}} mm" copy reads as a single symmetric tolerance, and two
      // near-identical list items when both sides breach would be noise,
      // not information. Not a missed case.
      warnings.push({
        level: 'warn',
        key: 'toleranceTooFine',
        params: { mm: round(Math.min(...breaching), 1), res: round(res, 3) },
      });
    }
  }

  return warnings;
}
